/**
 * Word-level diff (LCS) for the version history viewer. Good enough for
 * prose; falls back to "too large" above a size cap to stay responsive.
 */

export type DiffPart = { type: "same" | "added" | "removed"; text: string };

const MAX_CHARS = 60_000;

function tokenize(text: string): string[] {
  // words, whitespace runs, and single punctuation as separate tokens
  return text.match(/[\w'-]+|\s+|[^\w\s]/g) ?? [];
}

export function diffTexts(oldText: string, newText: string): DiffPart[] | null {
  if (oldText.length > MAX_CHARS || newText.length > MAX_CHARS) return null;
  const a = tokenize(oldText);
  const b = tokenize(newText);

  // classic LCS table (token counts are small enough after the cap)
  const n = a.length;
  const m = b.length;
  if (n * m > 4_000_000) return null;
  const lcs = new Uint32Array((n + 1) * (m + 1));
  const idx = (i: number, j: number) => i * (m + 1) + j;
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[idx(i, j)] = a[i] === b[j] ? lcs[idx(i + 1, j + 1)]! + 1 : Math.max(lcs[idx(i + 1, j)]!, lcs[idx(i, j + 1)]!);
    }
  }

  const parts: DiffPart[] = [];
  const push = (type: DiffPart["type"], text: string) => {
    const last = parts[parts.length - 1];
    if (last && last.type === type) last.text += text;
    else parts.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      push("same", a[i]!);
      i++;
      j++;
    } else if (lcs[idx(i + 1, j)]! >= lcs[idx(i, j + 1)]!) {
      push("removed", a[i]!);
      i++;
    } else {
      push("added", b[j]!);
      j++;
    }
  }
  while (i < n) push("removed", a[i++]!);
  while (j < m) push("added", b[j++]!);
  return parts;
}
